import type { Feature, Polygon, Position } from "geojson";
import type { MapTool, MapClickEvent } from "../../../application/map/tools/types";
import type { ToolDeps } from "./types";
import { parsePolygonId, geoJSONKeyForType } from "../../helpers/JobMap/editHelpers";
import { erasePolygonUseCase } from "../../../application/map/useCases/erasePolygonUseCase";

export function makeEraseTool(deps: ToolDeps): MapTool {
	let jobId: string | null = null;
	let rings: Position[][] | null = null;
	let typePrefix: string | null = null;
	let featureIndex = 0;
	const clicks: Position[] = [];

	return {
		id: "erase",
		label: "Erase",
		cursor: "crosshair",
		onActivate() {
			const polygonId = deps.getSelectedPolygonId();
			if (!polygonId) {
				deps.showError("Select a polygon to erase from");
				deps.deactivateTool();
				return;
			}

			const parsed = parsePolygonId(polygonId);
			if (!parsed) {
				deps.showError("Invalid polygon ID");
				deps.deactivateTool();
				return;
			}

			jobId = deps.getSelectedJobId();
			if (!jobId) {
				deps.showError("No job selected");
				deps.deactivateTool();
				return;
			}

			const geoKey = geoJSONKeyForType(parsed.typePrefix);
			const fc =
				geoKey === "boundaryGeoJSON"
					? deps.getBoundaryFC(jobId)
					: geoKey === "zonesGeoJSON"
						? deps.getZonesFC(jobId)
						: null;

			if (!fc) {
				deps.showError("Geometry data not found");
				deps.deactivateTool();
				return;
			}

			const feature = fc.features[parsed.featureIndex] as Feature<Polygon> | undefined;
			if (!feature || feature.geometry.type !== "Polygon") {
				deps.showError("Can only erase from Polygon features");
				deps.deactivateTool();
				return;
			}

			rings = feature.geometry.coordinates;
			typePrefix = parsed.typePrefix;
			featureIndex = parsed.featureIndex;
			clicks.length = 0;
		},
		onMapClick(e: MapClickEvent) {
			if (!rings || !jobId || !typePrefix) return;

			clicks.push([e.lngLat.lng, e.lngLat.lat]);
			deps.toolOverlay.addMarker([e.lngLat.lng, e.lngLat.lat]);

			if (clicks.length < 3) return;

			// 3 clicks collected — execute erase
			const [p1, pMid, p2] = clicks;
			const applyFC = typePrefix === "boundary" ? deps.applyBoundary : deps.applyZones;

			const result = erasePolygonUseCase(
				{ rings, p1, pMid, p2 },
				(newRings) => {
					const fc = deps.getBoundaryFC(jobId!) ?? deps.getZonesFC(jobId!);
					if (!fc) return;
					const updatedFeatures = [...fc.features];
					const updatedFeature: Feature<Polygon> = {
						...(updatedFeatures[featureIndex] as Feature<Polygon>),
						geometry: { type: "Polygon", coordinates: newRings },
					};
					updatedFeatures[featureIndex] = updatedFeature;
					applyFC(jobId!, { type: "FeatureCollection", features: updatedFeatures });
				},
				(oldRings) => {
					const fc = deps.getBoundaryFC(jobId!) ?? deps.getZonesFC(jobId!);
					if (!fc) return;
					const updatedFeatures = [...fc.features];
					const updatedFeature: Feature<Polygon> = {
						...(updatedFeatures[featureIndex] as Feature<Polygon>),
						geometry: { type: "Polygon", coordinates: oldRings },
					};
					updatedFeatures[featureIndex] = updatedFeature;
					applyFC(jobId!, { type: "FeatureCollection", features: updatedFeatures });
				},
			);

			if (!result.ok) {
				deps.showError(result.error);
			} else {
				deps.undoStack.push(result.data.command);
			}

			// Reset for next erase
			clicks.length = 0;
			deps.toolOverlay.clearOverlay();
		},
		onDeactivate() {
			clicks.length = 0;
			rings = null;
			jobId = null;
			typePrefix = null;
			deps.toolOverlay.clearOverlay();
		},
	};
}
